/** Anthropic-soft palette and layout tokens for Knowledge Agent chat UI. */

export const agentColors = {
  bgWarm: '#faf9f7',
  bgCard: '#ffffff',
  bgHero: 'linear-gradient(135deg, #faf9f7 0%, #f5f0eb 100%)',
  borderSoft: '#e8e4df',
  borderHero: '#ddd5cc',
  textPrimary: '#1c1917',
  textSecondary: '#78716c',
  textMuted: '#a8a29e',
  accent: '#c17f59',
  accentHover: '#a86b48',
  userBubble: '#44403c',
  assistantBubble: '#ffffff',
  assistantBorder: '#e7e5e4',
  statusActive: '#c17f59',
  statusDone: '#78716c',
  statusPending: '#d6d3d1',
  danger: '#b91c1c',
  overlay: 'rgba(28, 25, 23, 0.35)',
}

export const AGENT_ICON_SRC = `${process.env.PUBLIC_URL || ''}/agent/knowledge-assistant.svg`

export const agentHeroStyle = {
  marginBottom: 20,
  padding: '16px 20px',
  borderRadius: 12,
  border: `1px solid ${agentColors.borderHero}`,
  background: agentColors.bgHero,
  boxShadow: '0 2px 12px rgba(28, 25, 23, 0.05)',
}

/** 3D glass square card wrapper */
export const glassCardOuterStyle = {
  perspective: 900,
  width: '100%',
  maxWidth: 340,
  aspectRatio: '1 / 1',
}

export const glassCardInnerStyle = {
  position: 'relative',
  width: '100%',
  height: '100%',
  borderRadius: 20,
  background: 'linear-gradient(145deg, rgba(255,255,255,0.55) 0%, rgba(245,240,235,0.35) 100%)',
  backdropFilter: 'blur(18px) saturate(160%)',
  WebkitBackdropFilter: 'blur(18px) saturate(160%)',
  border: '1px solid rgba(255,255,255,0.65)',
  transition: 'transform 0.15s ease-out, box-shadow 0.15s ease-out',
  overflow: 'hidden',
  transformStyle: 'preserve-3d',
}

export const agentDrawerWidth = 720
export const agentAccessDrawerWidth = 400

/** Map permissions manifest clients to picker options. */
export function manifestClientsToOptions(manifest) {
  return (manifest?.clients ?? []).map((c) => ({
    id: c.id,
    client_name: c.name,
  }))
}

export function formatRelativeTime(iso) {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60000) return 'just now'
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`
  if (ms < 86400000) return `${Math.floor(ms / 3600000)}h ago`
  return new Date(iso).toLocaleDateString()
}

/** Flat list: most recently updated first. */
export function sortConversationsRecentFirst(conversations) {
  return [...(conversations ?? [])].sort((a, b) => {
    const ta = new Date(a.updated_at || a.created_at || 0).getTime()
    const tb = new Date(b.updated_at || b.created_at || 0).getTime()
    return tb - ta
  })
}

/** Move a conversation to the top with a fresh updated_at (optimistic UI). */
export function bumpConversationToTop(conversations, conversationId, patch = {}) {
  const now = new Date().toISOString()
  const idx = conversations.findIndex((c) => c.id === conversationId)
  if (idx === -1) {
    return sortConversationsRecentFirst([
      { id: conversationId, updated_at: now, ...patch },
      ...conversations,
    ])
  }
  const updated = { ...conversations[idx], updated_at: now, ...patch }
  const rest = conversations.filter((c) => c.id !== conversationId)
  return [updated, ...rest]
}

/** Shared frosted panel tokens (page + drawers). */
export const glassPanelStyle = {
  borderRadius: 16,
  background: 'linear-gradient(145deg, rgba(255,255,255,0.55) 0%, rgba(245,240,235,0.35) 100%)',
  backdropFilter: 'blur(18px) saturate(160%)',
  WebkitBackdropFilter: 'blur(18px) saturate(160%)',
  border: '1px solid rgba(255,255,255,0.65)',
  boxShadow: '0 4px 24px rgba(28, 25, 23, 0.06)',
}

export const glassPageBackground = {
  background: 'linear-gradient(160deg, #faf9f7 0%, #f5f0eb 50%, #faf9f7 100%)',
  minHeight: '100%',
}

export const glassInputStyle = {
  background: 'rgba(255,255,255,0.72)',
  border: '1px solid rgba(255,255,255,0.85)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
}

export const glassDrawerShellStyle = {
  background: 'linear-gradient(160deg, rgba(250,249,247,0.92) 0%, rgba(245,240,235,0.88) 100%)',
  backdropFilter: 'blur(20px) saturate(150%)',
  WebkitBackdropFilter: 'blur(20px) saturate(150%)',
  boxShadow: '-8px 0 32px rgba(28, 25, 23, 0.12)',
}

export const glassRailStyle = {
  background: 'rgba(255,255,255,0.28)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  borderRight: '1px solid rgba(255,255,255,0.45)',
}
